/* KEYBOARD SHORTCUTS.
 *
 * A DM runs this one-handed while talking, and the actions done hundreds of
 * times a session all need a small mouse target. There were none at all before
 * this.
 *
 * THE FEATURE IS THE GUARD, not the key map. A global keydown that takes "d"
 * while the DM is halfway through a session note is the abandonment bug the
 * project already knows about: the real criterion is "never interrupts typing",
 * not "fewer clicks". So the rules are deliberately timid:
 *
 *   - while the focus is in any field, NO bare key does anything. Not one.
 *     Escape included: the fields handle their own Escape locally and always
 *     have, and a global one would fight them.
 *   - a bare Space is not taken when a button or link has focus, because the
 *     browser is about to activate it and that is what the DM meant.
 *   - no combination with Ctrl, Cmd or Alt is taken except the one declared
 *     here, so nothing shadows a browser shortcut.
 *   - Ctrl/Cmd-K is allowed THROUGH a field, because it cannot be typed by
 *     accident and "search from wherever I am" is the point of it.
 *
 * Everything below is decided from the event alone, so it is testable without a
 * browser — which is the only reason to trust it, since the failure mode is a
 * keystroke the DM loses mid-sentence and never reports.
 */
export type Action =
  | "next" | "previous"   // the turn
  | "search"              // the palette
  | "pick"                // 1-9: reach the nth row's hit points
  | "close"               // Escape, for whatever is floating
  | "help";               // the cheat sheet

export interface Shortcut { action: Action; index?: number }

/** The parts of a KeyboardEvent this needs. Named so tests can hand it a
 *  literal instead of a DOM event. */
export interface KeyLike {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
  /** tagName of the focused element, upper case as the DOM reports it */
  tag?: string;
  /** whether the focused element is a contenteditable host */
  editable?: boolean;
  /** whether the focused element is a button, link or anything role="button" */
  pressable?: boolean;
  /** whether the key press has already been handled by something closer to it */
  defaultPrevented?: boolean;
}

const FIELDS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
export const isTyping = (e: KeyLike): boolean => Boolean(e.editable) || FIELDS.has(String(e.tag || "").toUpperCase());

/** What this key press means, or null for "leave it alone". */
export function resolve(e: KeyLike, enabled = true): Shortcut | null {
  if (!enabled) return null;
  if (e.defaultPrevented) return null;

  const mod = Boolean(e.ctrlKey || e.metaKey);

  /* The one combination that reaches through a field. */
  if (mod && !e.altKey && (e.key === "k" || e.key === "K")) return { action: "search" };

  /* Anything else with a modifier belongs to the browser or the OS. */
  if (mod || e.altKey) return null;

  /* ...and a bare key never fires while the DM is typing. */
  if (isTyping(e)) return null;

  switch (e.key) {
    case " ":
    case "Spacebar": // old Edge reported it this way; harmless to keep
      /* A focused button is about to be pressed by the browser. Let it. */
      if (e.pressable) return null;
      return { action: e.shiftKey ? "previous" : "next" };
    case "Escape":
      return { action: "close" };
    case "?":
      return { action: "help" };
    default: break;
  }

  /* 1-9 reach the nth row shown in the tracker. Deliberately not 0: there is no
     tenth row worth a key, and 0 is where a typo lands. */
  if (/^[1-9]$/.test(e.key) && !e.shiftKey) return { action: "pick", index: Number(e.key) };

  return null;
}

/** The cheat sheet, kept beside the resolver so the two cannot disagree about
 *  what the keys do. */
export const CHEAT_SHEET: { keys: string; does: string }[] = [
  { keys: "Space", does: "the next turn" },
  { keys: "Shift + Space", does: "back a turn" },
  { keys: "1 - 9", does: "that row's hit points, ready to type into" },
  { keys: "Ctrl / ⌘ + K", does: "search everything" },
  { keys: "Esc", does: "close what is open" },
  { keys: "?", does: "this list" },
];

/* WHY THERE IS NO "d" FOR DAMAGE AND NO "h" FOR HEAL.
 *
 * The roadmap asked for both. They turned out to be the same key as the row
 * number: every hit-point field in the console already reads "-10" as damage,
 * "+5" as a heal and "35" as a value, one rule for every number in the product.
 * So "3" then "-10" is already damage to the third row, and a separate damage
 * mode would be a second way to say it -- and a second thing to remember, which
 * is what that one-rule decision was made to avoid.
 *
 * Two single-letter keys also cost more than they look: they are the two most
 * likely to be struck while the DM believes they are typing, and the guard above
 * is only as good as the focus it can see. Not adding them is a smaller
 * promise, kept.
 */
